// CreateSingleBotModal.jsx (updated with Trades per day label and prevent outside click close)
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Switch,
  Collapse,
  Button,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp,
  TrendingDown,
  Bolt,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import { showToast } from "../../CoreDataService";

const COPYTRADINGSERVICE = "https://accounts.paybito.com/api/copyTrading";
const WEBSERVICE = "https://accounts.paybito.com/api/";

const genId = () =>
  Math.random().toString(36).substring(2, 10) +
  Math.random().toString(36).substring(2, 10);

// Validation helper functions
const validateBotName = (name) => {
  if (!name) return "Bot Name is required";
  if (name.length < 3) return "Bot Name must be at least 3 characters";
  if (name.length > 50) return "Bot Name must not exceed 50 characters";
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name))
    return "Bot Name can only contain letters, numbers, spaces, hyphens, and underscores";
  return "";
};

const validateBotDescription = (desc) => {
  if (!desc) return "Description is required";
  if (desc.length < 10) return "Description must be at least 10 characters";
  if (desc.length > 200) return "Description must not exceed 200 characters";
  return "";
};

// Function to validate number format with max 15 digits before decimal and 6 after decimal
const validateNumberFormat = (value, fieldName, allowZero = false) => {
  if (!value) return ""; // Optional fields handled separately

  // Check if it's a valid number
  if (isNaN(value) || value === '') return `${fieldName} must be a valid number`;

  const numValue = parseFloat(value);

  // Check if it's positive (if required)
  if (!allowZero && numValue <= 0) return `${fieldName} must be greater than 0`;
  if (allowZero && numValue < 0) return `${fieldName} cannot be negative`;

  // Check decimal places (max 6)
  const decimalPlaces = (value.toString().split('.')[1] || '').length;
  if (decimalPlaces > 6) return `${fieldName} can have maximum 6 decimal places`;

  // Check digits before decimal (max 15)
  const wholeNumberPart = value.toString().split('.')[0];
  if (wholeNumberPart && wholeNumberPart.length > 15)
    return `${fieldName} cannot exceed 15 digits before decimal`;

  return "";
};

const validateAmount = (amount, currencyBalance) => {
  if (!amount) return "Trade Size is required";

  const formatError = validateNumberFormat(amount, "Amount", false);
  if (formatError) return formatError;

  const numAmount = parseFloat(amount);

  // Check against available balance
  if (currencyBalance !== undefined && numAmount > currencyBalance) {
    return `Insufficient balance. Available: ${currencyBalance.toFixed(6)}`;
  }

  return "";
};

const validateMaxTrades = (trades) => {
  if (!trades) return ""; // Optional field

  // Check if it's a valid number
  if (isNaN(trades) || trades === '') return "Trades per day must be a valid number";

  const numTrades = parseFloat(trades);

  // Check if it's an integer
  if (!Number.isInteger(numTrades)) return "Trades per day must be a whole number";

  if (numTrades < 1) return "Trades per day must be at least 1";

  // Check digits (max 15)
  if (trades.toString().length > 15) return "Trades per day cannot exceed 15 digits";

  return "";
};

const validateMaxDailyCapital = (capital, currencyBalance) => {
  if (!capital) return ""; // Optional field

  const formatError = validateNumberFormat(capital, "Max Daily Capital", true);
  if (formatError) return formatError;

  const numCapital = parseFloat(capital);
  if (currencyBalance !== undefined && numCapital > currencyBalance) {
    return `Max Daily Capital cannot exceed available balance. Available: ${currencyBalance.toFixed(6)}`;
  }

  return "";
};

const validateProfitPercent = (percent, isRequired = false) => {
  if (isRequired && !percent) return "Profit percentage is required";
  if (!percent) return "";

  const formatError = validateNumberFormat(percent, "Profit %", false);
  if (formatError) return formatError;

  const numPercent = parseFloat(percent);
  if (numPercent > 100) return "Profit % cannot exceed 100%";

  return "";
};

const validatePortfolioPercent = (percent) => {
  if (!percent) return "Portfolio percentage is required";

  const formatError = validateNumberFormat(percent, "Portfolio %", false);
  if (formatError) return formatError;

  const numPercent = parseFloat(percent);
  if (numPercent > 100) return "Portfolio % cannot exceed 100%";

  return "";
};

const validateTotalPortfolio = (targets) => {
  const total = targets.reduce((sum, t) => sum + (parseFloat(t.portfolio) || 0), 0);
  if (total > 100) return "Total portfolio allocation cannot exceed 100%";
  return "";
};

const validateStopLoss = (slPct, isRequired = false) => {
  if (isRequired && !slPct) return "Stop Loss percentage is required";
  if (!slPct) return "";

  const formatError = validateNumberFormat(slPct, "Stop Loss %", false);
  if (formatError) return formatError;

  const numSl = parseFloat(slPct);
  if (numSl > 100) return "Stop Loss % cannot exceed 100%";

  return "";
};

// ── Sub-components ─────────────────────────────────────────────────────
const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index}>{value === index && children}</Box>
);

const SectionLabel = ({ children, mt = 2 }) => (
  <Typography variant="caption" sx={{
    display: "block", fontWeight: 800, letterSpacing: "0.14em",
    textTransform: "uppercase", mt, mb: 1.5, opacity: 0.6,
  }}>
    {children}
  </Typography>
);

const FieldRow = ({ children }) => <Box sx={{ mb: 1.5 }}>{children}</Box>;

const ExitToggleRow = ({ label, icon, checked, onChange, color }) => (
  <Box
    onClick={() => onChange(!checked)}
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      px: 2, py: 1.4,
      border: checked ? `1.5px solid ${color}55` : "1px solid",
      borderColor: checked ? `${color}55` : "divider",
      borderRadius: "12px",
      bgcolor: checked ? `${color}11` : "background.paper",
      cursor: "pointer", transition: "all 0.18s", mb: 0.75,
      "&:hover": { borderColor: `${color}44` },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: "8px",
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: checked ? `${color}22` : "action.hover",
        transition: "all 0.18s",
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color: checked ? color : "text.secondary" } })}
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{label}</Typography>
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
      size="small"
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: color },
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </Box>
);

const AlertRow = ({ label, value, onCopy, accent }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, letterSpacing: "0.1em",
      textTransform: "uppercase", opacity: 0.6, mb: 0.7,
    }}>
      {label}
    </Typography>
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      bgcolor: "action.hover", borderRadius: "10px",
      border: "1px solid", borderColor: "divider",
      px: 1.5, py: 0.9,
    }}>
      <Typography sx={{
        flex: 1, fontFamily: "'Roboto Mono', monospace",
        fontSize: "0.77rem", wordBreak: "break-all", lineHeight: 1.5,
      }}>
        {value}
      </Typography>
      <IconButton size="small" onClick={() => onCopy(value)} sx={{
        color: accent, flexShrink: 0,
        bgcolor: `${accent}15`, borderRadius: "7px", p: 0.55,
        "&:hover": { bgcolor: `${accent}28` },
      }}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  </Box>
);

const CreateSingleBotModal = ({ open, onClose, onBotCreated, editBotData }) => {
  const { brokerBasicInfo, btncolor } = useSelector((s) => s);
  const accent = btncolor || "#2563eb";
  const danger = "#ef4444";
  const isEditMode = !!editBotData;

  const [currencyBalanceList, setCurrencyBalanceList] = useState([]);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form fields
  const [botName, setBotName] = useState("");
  const [botDesc, setBotDesc] = useState("");
  const [exchange, setExchange] = useState("PAYBITO");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyList, setApiKeyList] = useState([]);
  const [pair, setPair] = useState("");
  const [pairList, setPairList] = useState([]);

  const [selectedBaseCurrency, setSelectedBaseCurrency] = useState("USDT");

  const [amount, setAmount] = useState("");
  const [tradesPerDay, setTradesPerDay] = useState(""); // Renamed from maxTrades
  const [maxDailyCapital, setMaxDailyCapital] = useState("");
  const [orderType, setOrderType] = useState("Market Order");
  const [botType, setBotType] = useState("MARKET");
  const [deviationPct, setDeviationPct] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  const [signal, setSignal] = useState("api_request");

  const [tpOn, setTpOn] = useState(false);
  const [targets, setTargets] = useState([{ pct: "", portfolio: "100" }]);
  const [slOn, setSlOn] = useState(false);
  const [slPct, setSlPct] = useState("");

  const [webhook, setWebhook] = useState("");
  const [enterLong, setEnterLong] = useState("");
  const [exitLong, setExitLong] = useState("");

  // Validation errors state
  const [errors, setErrors] = useState({
    botName: "",
    botDesc: "",
    amount: "",
    tradesPerDay: "", // Updated from maxTrades
    maxDailyCapital: "",
    deviationPct: "",
    expirationTime: "",
    tp1Pct: "",
    tp1Portfolio: "",
    tp2Pct: "",
    tp2Portfolio: "",
    totalPortfolio: "",
    slPct: "",
    pair: "",
  });

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    authorization: `BEARER ${localStorage.getItem("access_token")}`,
  });
  const getCustomerId = () => parseInt(localStorage.getItem("userId"));

  // Fetch user balance
  const getUserTransaction = async () => {
    const payload = {
      customerId: localStorage.getItem("userId"),
      uuid: localStorage.getItem("uuid"),
    };

    setLoadingBalance(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}transaction/getUserBalance`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      if (response.data.error.error_data !== 0) {
        showToast("error", "Cannot fetch user balance");
        console.log("Fetching User balance failed");
      } else {
        const balanceList = response.data.userBalanceList || [];
        setCurrencyBalanceList(balanceList);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast("warning", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Failed to fetch user balance");
      }
    } finally {
      setLoadingBalance(false);
    }
  };

  // Get balance for selected currency
  const getBalanceForCurrency = (currencyCode) => {
    const balanceItem = currencyBalanceList.find(item => item.currencyCode === currencyCode);
    return balanceItem ? balanceItem.closingBalance : 0;
  };

  // Load edit data when in edit mode
  useEffect(() => {
    if (editBotData && open) {
      setBotName(editBotData.name || "");
      setBotDesc(editBotData.description || "");
      setExchange(editBotData.exchange || "PAYBITO");

      // Format asset pair for dropdown
      if (editBotData.assetPair) {
        const formattedPair = editBotData.assetPair.replace(/(.{3,4})(?=.)/, "$1-");
        setPair(formattedPair);
      }

      setAmount(editBotData.amountPerTrade?.toString() || "");
      setTradesPerDay(editBotData.maxTrades?.toString() || ""); // Updated variable name
      setMaxDailyCapital(editBotData.maxDailyCapitalExposure?.toString() || "");
      setOrderType(editBotData.orderType === 1 ? "Market Order" : "Limit Order");
      setBotType(editBotData.botType || (editBotData.orderType === 1 ? "MARKET" : "LIMIT"));
      setDeviationPct(editBotData.deviationPct?.toString() || "");
      setExpirationTime(editBotData.expirationTime?.toString() || "");
      setSignal("api_request");

      // Set webhook and alert strings from editBotData
      if (editBotData.webhookUrl) {
        setWebhook(editBotData.webhookUrl);
      }
      if (editBotData.enterLong) {
        setEnterLong(editBotData.enterLong);
      }
      if (editBotData.exitLong) {
        setExitLong(editBotData.exitLong);
      }

      // Set TP/SL states
      setTpOn(editBotData.isTpEnabled === 1);
      if (editBotData.isTpEnabled === 1) {
        const newTargets = [];
        if (editBotData.tp1TargetPct) {
          newTargets.push({
            pct: editBotData.tp1TargetPct?.toString() || "",
            portfolio: editBotData.tp1PortfolioPct?.toString() || "100"
          });
        }
        if (editBotData.tp2TargetPct && editBotData.tp2TargetPct > 0) {
          newTargets.push({
            pct: editBotData.tp2TargetPct?.toString() || "",
            portfolio: editBotData.tp2PortfolioPct?.toString() || "50"
          });
        }
        setTargets(newTargets.length ? newTargets : [{ pct: "", portfolio: "100" }]);
      }

      setSlOn(editBotData.isSlEnabled === 1);
      setSlPct(editBotData.slTargetPct?.toString() || "");

      setSelectedBaseCurrency(editBotData.amountType || "USDT");
    }
  }, [editBotData, open]);

  // ── Alert string generation ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode && botName && exchange && pair) {
      const uid = genId().substring(0, 16);
      const base = pair.replace("-", "");
      setWebhook(`https://api.paybito.com/${uid}`);
      setEnterLong(`ENTER-LONG-${exchange}-${botName}-${base}-${uid}`);
      setExitLong(`EXIT-LONG-${exchange}-${botName}-${base}-${uid}`);
    }
  }, [botName, exchange, pair, isEditMode]);

  // Fetch data when modal opens
  useEffect(() => {
    if (open) {
      fetchApiKeys();
      fetchPairs();
      getUserTransaction(); // Fetch user balance
      // Reset errors when opening
      setErrors({});
    }
  }, [open]);

  // After pairList is loaded and we're in edit mode, try to find and set the correct pair
  useEffect(() => {
    if (isEditMode && editBotData?.assetPair && pairList.length > 0) {
      const matchingPair = pairList.find(p =>
        p.assetPair === editBotData.assetPair ||
        `${p.currency}-${p.baseCurrency}` === editBotData.assetPair.replace(/(.{3,4})(?=.)/, "$1-")
      );

      if (matchingPair) {
        setPair(`${matchingPair.currency}-${matchingPair.baseCurrency}`);
        setSelectedBaseCurrency(matchingPair.baseCurrency);
      }
    }
  }, [pairList, isEditMode, editBotData]);

  // Update base currency when pair changes
  useEffect(() => {
    if (pair) {
      const selectedPair = pairList.find(p => `${p.currency}-${p.baseCurrency}` === pair);
      if (selectedPair) {
        setSelectedBaseCurrency(selectedPair.baseCurrency);
      }
    }
  }, [pair, pairList]);

  // Validate fields on change
  useEffect(() => {
    const newErrors = { ...errors };

    // Pair validation
    newErrors.pair = pair ? "" : "Please select an asset pair";

    // Bot Name validation
    newErrors.botName = validateBotName(botName);

    // Bot Description validation
    newErrors.botDesc = validateBotDescription(botDesc);

    // Amount validation with balance check
    const currencyBalance = getBalanceForCurrency(selectedBaseCurrency);
    newErrors.amount = validateAmount(amount, currencyBalance);

    // Trades per day validation (updated)
    newErrors.tradesPerDay = validateMaxTrades(tradesPerDay);

    // Max Daily Capital validation
    newErrors.maxDailyCapital = validateMaxDailyCapital(maxDailyCapital, currencyBalance);

    // Limit order fields validation
    if (botType === "LIMIT") {
      if (!deviationPct) {
        newErrors.deviationPct = "Deviation percentage is required";
      } else if (isNaN(deviationPct) || parseFloat(deviationPct) <= 0) {
        newErrors.deviationPct = "Deviation must be a positive number";
      } else if (parseFloat(deviationPct) > 100) {
        newErrors.deviationPct = "Deviation cannot exceed 100%";
      } else {
        newErrors.deviationPct = "";
      }

      if (!expirationTime) {
        newErrors.expirationTime = "Expiration time is required";
      } else if (!Number.isInteger(Number(expirationTime)) || parseInt(expirationTime) < 5 || parseInt(expirationTime) > 20160) {
        newErrors.expirationTime = "Valid range: 5 to 20160 minutes";
      } else {
        newErrors.expirationTime = "";
      }
    } else {
      newErrors.deviationPct = "";
      newErrors.expirationTime = "";
    }

    // TP validations
    if (tpOn) {
      newErrors.tp1Pct = validateProfitPercent(targets[0]?.pct, true);
      newErrors.tp1Portfolio = validatePortfolioPercent(targets[0]?.portfolio);

      if (targets.length > 1) {
        newErrors.tp2Pct = validateProfitPercent(targets[1]?.pct, true);
        newErrors.tp2Portfolio = validatePortfolioPercent(targets[1]?.portfolio);
      } else {
        newErrors.tp2Pct = "";
        newErrors.tp2Portfolio = "";
      }

      newErrors.totalPortfolio = validateTotalPortfolio(targets);
    } else {
      newErrors.tp1Pct = "";
      newErrors.tp1Portfolio = "";
      newErrors.tp2Pct = "";
      newErrors.tp2Portfolio = "";
      newErrors.totalPortfolio = "";
    }

    // SL validation
    if (slOn) {
      newErrors.slPct = validateStopLoss(slPct, true);
    } else {
      newErrors.slPct = "";
    }

    setErrors(newErrors);
  }, [botName, botDesc, amount, tradesPerDay, maxDailyCapital, botType, deviationPct, expirationTime, tpOn, targets, slOn, slPct, selectedBaseCurrency, currencyBalanceList]);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${COPYTRADINGSERVICE}/bot/GetApiKey`,
        JSON.stringify({
          customerId: getCustomerId(),
          uuid: localStorage.getItem("uuid"),
          marketType: 'SPOT',
          exchange_name: exchange
        }),
        { headers: getAuthHeaders() }
      );
      const result = r.data;
      if (result.error.error_data !== 0) showToast("error", result.error.error_msg);
      else setApiKeyList(result.keyDetails || []);

      if (isEditMode && editBotData?.apiKeyId) {
        const matchingKey = result.keyDetails?.find(k => k.apiKeyId === editBotData.apiKeyId);
        if (matchingKey) {
          setApiKey(matchingKey);
        }
      }
    } catch {
      showToast("error", "Failed to fetch API keys");
    }
    finally { setLoading(false); }
  };

  const fetchPairs = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${COPYTRADINGSERVICE}/bot/GetAssetPairs`,
        JSON.stringify({
          marketType: 1,
          txnType: 1,
          isUpdate: 0,
          customerId: getCustomerId(),
          uuid: localStorage.getItem("uuid"),
          exchange_name: exchange
        }),
        { headers: getAuthHeaders() }
      );

      const raw = r.data.getAssetPairs || [];
      const sorted = [...raw].sort((a, b) =>
        String(a.baseCurrency).localeCompare(String(b.baseCurrency))
      );
      setPairList(sorted);
    } catch (error) {
      console.error("Failed to fetch pairs:", error);
      showToast("error", "Failed to fetch pairs");
    }
    finally { setLoading(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); showToast("success", "Copied!"); };

  const addTarget = () => {
    if (targets.length < 2) {
      setTargets([...targets, { pct: "", portfolio: "50" }]);
    }
  };

  const removeTarget = (i) => {
    if (targets.length > 1) {
      setTargets(targets.filter((_, x) => x !== i));
    }
  };

  const updateTarget = (i, f, v) => {
    const a = [...targets];
    a[i] = { ...a[i], [f]: v };
    setTargets(a);
  };

  const validateAll = () => {
    const requiredErrors = [
      errors.pair,
      errors.botName,
      errors.botDesc,
      errors.amount,
      errors.deviationPct,
      errors.expirationTime,
      errors.tp1Pct,
      errors.tp1Portfolio,
      errors.tp2Pct,
      errors.tp2Portfolio,
      errors.totalPortfolio,
      errors.slPct,
    ].filter(error => error && error.length > 0);

    return requiredErrors.length === 0;
  };

  const handleCreate = async () => {
    if (!validateAll()) {
      showToast("error", "Please fix all validation errors before submitting");
      return;
    }

    setCreating(true);

    try {
      const formattedPair = pair.replace("-", "");

      const payload = {
        customerId: getCustomerId(),
        uuid: localStorage.getItem("uuid"),
        name: botName,
        description: botDesc,
        exchange: exchange,
        apiKeyId: apiKey?.apiKeyId,
        assetPair: formattedPair,
        startCondition: "API Request",
        orderType: orderType === "Market Order" ? 1 : 2,
        botType: botType,
        deviationPct: botType === "LIMIT" ? parseFloat(deviationPct) : 0,
        expirationTime: botType === "LIMIT" ? parseInt(expirationTime) : 0,
        amountPerTrade: amount ? parseFloat(amount) : 0,
        amountType: selectedBaseCurrency,
        isTpEnabled: tpOn ? 1 : 0,
        tp1TargetPct: tpOn ? parseFloat(targets[0]?.pct || 0) : 0,
        tp2TargetPct: tpOn && targets[1] ? parseFloat(targets[1]?.pct || 0) : 0,
        tp1PortfolioPct: tpOn ? parseFloat(targets[0]?.portfolio || 0) : 0,
        tp2PortfolioPct: tpOn && targets[1] ? parseFloat(targets[1]?.portfolio || 0) : 0,
        isSlEnabled: slOn ? 1 : 0,
        slTargetPct: slOn ? parseFloat(slPct || 0) : 0,
        maxTrades: tradesPerDay ? parseInt(tradesPerDay) : 0, // Send as maxTrades in API
        maxDailyCapitalExposure: maxDailyCapital ? parseFloat(maxDailyCapital) : 0,
        action: isEditMode ? "UPDATE" : "INSERT",
      };

      if (isEditMode) {
        payload.botId = editBotData.botId;
      }

      const r = await axios.post(
        `${COPYTRADINGSERVICE}/bot/createSignalBot`,
        JSON.stringify(payload),
        { headers: getAuthHeaders() }
      );

      const result = r.data;
      if (result.error.error_data === 0) {
        showToast("success", result.error.error_msg || `Signal bot ${isEditMode ? 'updated' : 'created'} successfully!`);

        if (!isEditMode) {
          if (result.webhookUrl) setWebhook(result.webhookUrl);
          if (result.enterLong) setEnterLong(result.enterLong);
          if (result.exitLong) setExitLong(result.exitLong);
          setActiveTab(1);
        }

        if (onBotCreated) onBotCreated(result);

        if (isEditMode) {
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      } else {
        showToast("error", result.error.error_msg);
      }
    } catch {
      showToast("error", `Failed to ${isEditMode ? 'update' : 'create'} bot`);
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    setBotName("");
    setBotDesc("");
    setApiKey("");
    setPair("");
    setOrderType("Market Order");
    setBotType("MARKET");
    setDeviationPct("");
    setExpirationTime("");
    setAmount("");
    setTradesPerDay(""); // Updated
    setMaxDailyCapital("");
    setSignal("api_request");
    setTpOn(false);
    setTargets([{ pct: "", portfolio: "100" }]);
    setSlOn(false);
    setSlPct("");
    setSelectedBaseCurrency("USDT");
    setActiveTab(0);
    setWebhook("");
    setEnterLong("");
    setExitLong("");
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const dialogTitle = isEditMode ? "Edit Signal Bot" : "Create Signal Bot";

  // Get current balance for display
  const currentBalance = getBalanceForCurrency(selectedBaseCurrency);

  // Get LTP for selected pair
  const selectedPairData = pair ? pairList.find(p => `${p.currency}-${p.baseCurrency}` === pair) : null;
  const selectedLtp = selectedPairData?.ltp;

  const F = {
    "& .MuiInputLabel-root.Mui-focused": { color: accent },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "&:hover fieldset": { borderColor: accent + "66" },
      "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1.5px" },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close when the close button is clicked, not on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return; // Don't close
        }
        handleClose(); // This will be called by the close button
      }}
      disableEscapeKeyDown // Prevent ESC key from closing
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundImage: "none",
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0, px: 3, pt: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: "11px",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${accent} 0%, ${accent}bb 100%)`,
              boxShadow: `0 4px 12px ${accent}44`,
              flexShrink: 0,
            }}>
              <Bolt sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {dialogTitle}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6, mt: 0.2, display: "block" }}>
                {isEditMode ? "Update your automated trading bot" : "Configure your automated trading bot"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{
            borderRadius: "9px", p: 0.65,
            "&:hover": { bgcolor: "action.hover" },
          }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 38,
            "& .MuiTab-root": {
              minHeight: 38, fontSize: "0.8rem", fontWeight: 600,
              textTransform: "none", px: 2, py: 0,
            },
            "& .Mui-selected": { color: accent },
            "& .MuiTabs-indicator": {
              backgroundColor: accent, height: "2.5px",
              borderRadius: "2px 2px 0 0",
            },
          }}
        >
          <Tab label="Settings" />
          <Tab label="Alerts" disabled={!webhook && !enterLong && !exitLong} />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{
        px: 3, py: 2.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { borderRadius: 4, bgcolor: "divider" },
      }}>

        {/* SETTINGS TAB */}
        <TabPanel value={activeTab} index={0}>
          <SectionLabel mt={0}>General</SectionLabel>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Bot Name"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder="e.g. BTC Scalper"
              sx={F}
              error={!!errors.botName}
              helperText={errors.botName || "3-50 chars, letters, numbers, spaces, hyphens, underscores"}
            />
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={botDesc}
              onChange={(e) => setBotDesc(e.target.value)}
              placeholder="Describe what this bot does"
              multiline
              rows={2}
              sx={F}
              error={!!errors.botDesc}
              helperText={errors.botDesc || "10-200 characters"}
            />
          </FieldRow>

          <FieldRow>
            <FormControl fullWidth size="small" sx={F}>
              <InputLabel>Exchange</InputLabel>
              <Select value={exchange} label="Exchange" disabled>
                <MenuItem value="PAYBITO">{brokerBasicInfo?.exchange || "Paybito"}</MenuItem>
              </Select>
            </FormControl>
          </FieldRow>

          <FieldRow>
            <FormControl fullWidth size="small" sx={F}>
              <InputLabel>Market Type</InputLabel>
              <Select value="Spot" label="Market Type" disabled>
                <MenuItem value="Spot">Spot</MenuItem>
              </Select>
            </FormControl>
          </FieldRow>

          <FieldRow>
            <FormControl fullWidth size="small" sx={F}>
              <InputLabel>
                {loading && !apiKeyList.length ? "Loading keys…" : "API Key"}
              </InputLabel>
              <Select
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                label={loading && !apiKeyList.length ? "Loading keys…" : "API Key"}
                disabled={!exchange || loading}
              >
                <MenuItem value="">
                  {!exchange ? "Select exchange first"
                    : apiKeyList.length === 0 && !loading ? "No API keys found"
                      : "Select API Key"}
                </MenuItem>
                {apiKeyList.map((k) => (
                  <MenuItem key={k.apiKeyId} value={k}>{k.apiKeyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldRow>

          <FieldRow>
            <FormControl fullWidth size="small" sx={F} error={!!errors.pair}>
              <InputLabel>
                {loading && !pairList.length ? "Loading pairs…" : "Pair"}
              </InputLabel>
              <Select
                value={pair}
                onChange={(e) => setPair(e.target.value)}
                label={loading && !pairList.length ? "Loading pairs…" : "Pair"}
                disabled={!exchange || loading}
              >
                <MenuItem value="">
                  {!exchange ? "Select exchange first"
                    : pairList.length === 0 && !loading ? "No pairs found"
                      : "Select Pair"}
                </MenuItem>
                {pairList.map((p, i) => (
                  <MenuItem key={i} value={`${p.currency}-${p.baseCurrency}`}>
                    {p.currency}-{p.baseCurrency}
                  </MenuItem>
                ))}
              </Select>
              {errors.pair && <FormHelperText>{errors.pair}</FormHelperText>}
            </FormControl>
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Trade Size"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              InputProps={{
                endAdornment: <InputAdornment position="end">{selectedBaseCurrency}</InputAdornment>
              }}
              sx={F}
              error={!!errors.amount}
              helperText={
                errors.amount ||
                `Available: ${currentBalance.toFixed(6)} ${selectedBaseCurrency} | Max 15 digits, 6 decimals`
              }
            />
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Trades per day" // Updated label
              value={tradesPerDay}
              onChange={(e) => setTradesPerDay(e.target.value)}
              placeholder="Enter max trades per day" // Updated placeholder
              sx={F}
              error={!!errors.tradesPerDay} // Updated error reference
              helperText={errors.tradesPerDay || "whole numbers only, min 1, max 15 digits"} // Updated helper text
            />
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Daily Capital Exposure"
              value={maxDailyCapital}
              onChange={(e) => setMaxDailyCapital(e.target.value)}
              placeholder="Enter max daily capital"
              InputProps={{ endAdornment: <InputAdornment position="end">{selectedBaseCurrency}</InputAdornment> }}
              sx={F}
              error={!!errors.maxDailyCapital}
              helperText={errors.maxDailyCapital || `max 15 digits, 6 decimals`}
            />
          </FieldRow>

          <SectionLabel>Order</SectionLabel>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {[
              { val: "Market Order", label: "Market", bType: "MARKET", disabled: false },
              { val: "Limit Order", label: "Limit", bType: "LIMIT", disabled: false },
            ].map(({ val, label, bType, disabled }) => {
              const sel = orderType === val;
              return (
                <Box key={val} onClick={() => { if (!disabled) { setOrderType(val); setBotType(bType); } }} sx={{
                  flex: 1, py: 1.2, px: 1.5, borderRadius: "10px",
                  cursor: disabled ? "not-allowed" : "pointer",
                  border: sel ? `1.5px solid ${accent}` : "1px solid",
                  borderColor: sel ? accent : "divider",
                  bgcolor: sel ? `${accent}12` : "action.hover",
                  opacity: disabled ? 0.4 : 1,
                  display: "flex", alignItems: "center", gap: 1,
                  transition: "all 0.15s",
                  "&:hover": !disabled ? { borderColor: `${accent}66` } : {},
                }}>
                  <Box sx={{
                    width: 15, height: 15, borderRadius: "50%", flexShrink: 0,
                    border: sel ? `2px solid ${accent}` : "2px solid",
                    borderColor: sel ? accent : "text.secondary",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sel && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: accent }} />}
                  </Box>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: sel ? 600 : 400, color: sel ? accent : "text.primary" }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Collapse in={botType === "LIMIT"}>
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <TextField
                size="small"
                type="number"
                label="Deviation from Last price"
                value={deviationPct}
                onChange={(e) => setDeviationPct(e.target.value)}
                placeholder="Enter deviation %"
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>,
                }}
                sx={{ flex: 1, ...F }}
                error={!!errors.deviationPct}
                helperText={errors.deviationPct}
              />
              <TextField
                size="small"
                type="number"
                label="Time in force"
                value={expirationTime}
                onChange={(e) => setExpirationTime(e.target.value)}
                placeholder="5"
                InputProps={{
                  endAdornment: <InputAdornment position="end">min</InputAdornment>,
                }}
                sx={{ flex: 1, ...F }}
                error={!!errors.expirationTime}
                helperText={errors.expirationTime || "Valid range: 5 to 20160 minutes"}
              />
            </Box>
            {pair && selectedLtp !== undefined && (
              <Typography variant="caption" sx={{ display: "block", mb: 1.5, ml: 0.5, opacity: 0.7 }}>
                Last Traded Price: <strong>{selectedLtp} {selectedBaseCurrency}</strong>
              </Typography>
            )}
          </Collapse>

          <SectionLabel>Signal Type</SectionLabel>
          <Box sx={{
            py: 1.2, px: 1.5, mb: 1.5,
            borderRadius: "10px",
            border: `1px solid ${accent}`,
            bgcolor: `${accent}12`,
            display: "flex", alignItems: "center", gap: 1,
          }}>
            <Box sx={{
              width: 15, height: 15, borderRadius: "50%", flexShrink: 0,
              border: `2px solid ${accent}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: accent }} />
            </Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: accent }}>
              API Request
            </Typography>
          </Box>

          <SectionLabel>Exits</SectionLabel>

          <ExitToggleRow label="Take Profit" icon={<TrendingUp />}
            checked={tpOn} onChange={setTpOn} color={accent} />

          <Collapse in={tpOn}>
            <Box sx={{
              mx: 0.25, mb: 1.5, p: 2, borderRadius: "12px",
              border: `1px solid ${accent}33`,
              bgcolor: `${accent}08`,
            }}>
              {targets.map((t, idx) => (
                <Box key={idx} sx={{ mb: idx < targets.length - 1 ? 2 : 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.08em", opacity: 0.6 }}>
                      TARGET {idx + 1}
                    </Typography>
                    {idx > 0 && (
                      <IconButton size="small" onClick={() => removeTarget(idx)} sx={{
                        color: danger, bgcolor: `${danger}15`, borderRadius: "6px", p: 0.35,
                        "&:hover": { bgcolor: `${danger}28` },
                      }}>
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Profit %"
                      value={t.pct}
                      onChange={(e) => updateTarget(idx, "pct", e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ flex: 1, ...F }}
                      error={idx === 0 ? !!errors.tp1Pct : !!errors.tp2Pct}
                      helperText={idx === 0 ? errors.tp1Pct : errors.tp2Pct}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Portfolio %"
                      value={t.portfolio}
                      onChange={(e) => updateTarget(idx, "portfolio", e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ flex: 1, ...F }}
                      error={idx === 0 ? !!errors.tp1Portfolio : !!errors.tp2Portfolio}
                      helperText={idx === 0 ? errors.tp1Portfolio : errors.tp2Portfolio}
                    />
                  </Box>
                </Box>
              ))}

              {targets.length < 2 && (
                <Button size="small" onClick={addTarget}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    mt: 1.5, fontSize: "0.74rem", fontWeight: 600,
                    textTransform: "none", color: accent,
                    border: `1px dashed ${accent}55`, borderRadius: "8px", px: 1.5, py: 0.5,
                    "&:hover": { bgcolor: `${accent}10`, borderColor: accent },
                  }}>
                  Add Target 2
                </Button>
              )}

              {errors.totalPortfolio && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                  {errors.totalPortfolio}
                </Typography>
              )}
            </Box>
          </Collapse>

          <ExitToggleRow label="SL (Stop Loss)" icon={<TrendingDown />}
            checked={slOn} onChange={setSlOn} color={danger} />

          <Collapse in={slOn}>
            <Box sx={{
              mx: 0.25, mb: 1.5, p: 2, borderRadius: "12px",
              border: `1px solid ${danger}33`,
              bgcolor: `${danger}08`,
            }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Stop Loss %"
                value={slPct}
                onChange={(e) => setSlPct(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ ...F }}
                error={!!errors.slPct}
                helperText={errors.slPct}
              />
            </Box>
          </Collapse>
        </TabPanel>

        {/* ALERTS TAB */}
        <TabPanel value={activeTab} index={1}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: "12px", fontSize: "0.78rem" }}>
  Use these details to configure alerts in your API request strategy.{" "}
  <strong>'Enter Long'</strong> or <strong>'Exit Long'</strong> comments need to be passed
  in the JSON body when hitting the API.
  <Box
    component="pre"
    sx={{
      mt: 1,
      mb: 0,
      p: 1,
      backgroundColor: "rgba(0,0,0,0.06)",
      borderRadius: "6px",
      fontSize: "0.75rem",
      fontFamily: "monospace",
      overflowX: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-all",
    }}
  >
    {`{\n  "comment": "Ex: EXIT-LONG_paybito_mybot_7"\n}`}
  </Box>
</Alert>

          <AlertRow label="Webhook URL" value={webhook} onCopy={copy} accent={accent} />
          <AlertRow label="Enter Long" value={enterLong} onCopy={copy} accent={accent} />
          <AlertRow label="Exit Long" value={exitLong} onCopy={copy} accent={accent} />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5, justifyContent: "flex-end" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none", fontSize: "0.84rem", fontWeight: 600,
            borderRadius: "10px", px: 2.5,
          }}
        >
          Cancel
        </Button>

        {activeTab === 0 ? (
          <Button
            onClick={handleCreate}
            disabled={creating || loading || loadingBalance || Object.values(errors).some(error => error && error.length > 0)}
            sx={{
              textTransform: "none", fontSize: "0.84rem", fontWeight: 700,
              color: "#fff", borderRadius: "10px", px: 3,
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              boxShadow: `0 4px 14px ${accent}44`,
              "&:hover": { background: `linear-gradient(135deg, ${accent}ee, ${accent}aa)` },
              "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
            }}
            startIcon={creating ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
          >
            {creating ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Bot" : "Create Bot")}
          </Button>
        ) : (
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none", fontSize: "0.84rem", fontWeight: 700,
              color: "#fff", borderRadius: "10px", px: 3,
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              boxShadow: `0 4px 14px ${accent}44`,
              "&:hover": { background: `linear-gradient(135deg, ${accent}ee, ${accent}aa)` },
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateSingleBotModal;